// delivery/shipment-manifest.schema.ts
import { z } from "zod";
var DeliveryShipmentPlanNodeTypeSchema = z.enum([
  "origin",
  "hub",
  "destination",
  "return_origin"
]);
var DeliveryShipmentPartitionAllocationStateSchema = z.enum([
  "planned",
  "in_transit",
  "delivered",
  "cancelled"
]);
var DeliveryShipmentManifestStatusSchema = z.enum([
  "draft",
  "ready",
  "submitted",
  "departed",
  "arrived",
  "voided"
]);
var DeliveryShipmentManifestFlowSchema = z.enum([
  "templates_outgoing",
  "external_incoming"
]);
var NodeAddressSnapshotSchema = z.object({
  address1: z.string(),
  address2: z.string().nullable().optional(),
  city: z.string(),
  province: z.string(),
  postalCode: z.string(),
  countryCode: z.string().optional()
});
var HubLayoverScheduleSchema = z.object({
  checkInAt: z.string().nullable(),
  checkOutAt: z.string().nullable()
});
var HubNodeMetadataSchema = z.object({
  address: NodeAddressSnapshotSchema.optional(),
  layoverSchedule: HubLayoverScheduleSchema.optional()
});
var DeliveryShipmentPlanNodeSchema = z.object({
  id: z.string().uuid(),
  shipmentId: z.string().uuid(),
  nodeType: DeliveryShipmentPlanNodeTypeSchema,
  name: z.string(),
  stockLocationId: z.string().uuid().nullable(),
  complianceLicenseId: z.string().uuid().nullable(),
  organizationLocationId: z.string().uuid().nullable(),
  arrivalDeadline: z.date().nullable(),
  metadata: z.record(z.unknown()),
  createdAt: z.date(),
  updatedAt: z.date()
});
var CreateDeliveryShipmentPlanNodeSchema = z.object({
  shipmentId: z.string().uuid(),
  nodeType: DeliveryShipmentPlanNodeTypeSchema,
  name: z.string().min(1),
  stockLocationId: z.string().uuid().nullable().optional(),
  complianceLicenseId: z.string().uuid().nullable().optional(),
  organizationLocationId: z.string().uuid().nullable().optional(),
  arrivalDeadline: z.date().nullable().optional(),
  metadata: z.record(z.unknown()).optional()
});
var UpdateDeliveryShipmentPlanNodeSchema = z.object({
  nodeType: DeliveryShipmentPlanNodeTypeSchema.optional(),
  name: z.string().min(1).optional(),
  stockLocationId: z.string().uuid().nullable().optional(),
  complianceLicenseId: z.string().uuid().nullable().optional(),
  organizationLocationId: z.string().uuid().nullable().optional(),
  arrivalDeadline: z.date().nullable().optional(),
  metadata: z.record(z.unknown()).optional()
});
var DeliveryShipmentPartitionAllocationSchema = z.object({
  id: z.string().uuid(),
  shipmentId: z.string().uuid(),
  partitionId: z.string().uuid(),
  destinationNodeId: z.string().uuid(),
  allocationState: DeliveryShipmentPartitionAllocationStateSchema,
  metadata: z.record(z.unknown()),
  createdAt: z.date(),
  updatedAt: z.date()
});
var CreateDeliveryShipmentPartitionAllocationSchema = z.object({
  shipmentId: z.string().uuid(),
  partitionId: z.string().uuid(),
  destinationNodeId: z.string().uuid(),
  allocationState: DeliveryShipmentPartitionAllocationStateSchema.optional(),
  metadata: z.record(z.unknown()).optional()
});
var UpdateDeliveryShipmentPartitionAllocationSchema = z.object({
  destinationNodeId: z.string().uuid().optional(),
  allocationState: DeliveryShipmentPartitionAllocationStateSchema.optional(),
  metadata: z.record(z.unknown()).optional()
});
var ShipmentManifestTransporterDetailSchema = z.object({
  driverOccupationalLicenseNumber: z.string().nullable().optional(),
  driverName: z.string().nullable().optional(),
  driverLicenseNumber: z.string().nullable().optional(),
  driverLayoverLeg: z.string().nullable().optional(),
  vehicleMake: z.string().nullable().optional(),
  vehicleModel: z.string().nullable().optional(),
  vehicleLicensePlateNumber: z.string().nullable().optional(),
  vehicleRegistrationNumber: z.string().nullable().optional()
});
var ShipmentManifestTransporterSchema = z.object({
  transporterFacilityLicenseNumber: z.string().nullable().optional(),
  transporterFacilityName: z.string().nullable().optional(),
  driverName: z.string().nullable().optional(),
  driverOccupationalLicenseNumber: z.string().nullable().optional(),
  driverLicenseNumber: z.string().nullable().optional(),
  vehicleMake: z.string().nullable().optional(),
  vehicleModel: z.string().nullable().optional(),
  vehicleLicensePlateNumber: z.string().nullable().optional(),
  vehicleRegistrationNumber: z.string().nullable().optional(),
  isLayover: z.boolean().optional(),
  driverLayoverLeg: z.string().nullable().optional(),
  phoneNumberForQuestions: z.string().nullable().optional(),
  estimatedDepartureDateTime: z.string().nullable().optional(),
  estimatedArrivalDateTime: z.string().nullable().optional(),
  transporterDetails: z.array(ShipmentManifestTransporterDetailSchema).optional()
});
var ShipmentManifestPackageSchema = z.object({
  packageLabel: z.string().min(1),
  quantity: z.number().nonnegative().optional(),
  unitOfMeasureName: z.string().nullable().optional(),
  wholesalePrice: z.number().nullable().optional(),
  grossWeight: z.number().nullable().optional(),
  grossUnitOfWeightName: z.string().nullable().optional(),
  productName: z.string().nullable().optional(),
  metadata: z.record(z.unknown()).default({})
});
var ShipmentManifestDestinationSchema = z.object({
  recipientFacilityLicenseNumber: z.string().nullable().optional(),
  recipientFacilityName: z.string().nullable().optional(),
  invoiceNumber: z.string().nullable().optional(),
  transferTypeName: z.string().nullable().optional(),
  plannedRoute: z.string().nullable().optional(),
  estimatedDepartureAt: z.string().nullable().optional(),
  estimatedArrivalAt: z.string().nullable().optional(),
  grossWeight: z.number().nullable().optional(),
  grossUnitOfWeightId: z.string().nullable().optional(),
  /** Transporters for this destination (encodes layover driver changes) */
  transporters: z.array(ShipmentManifestTransporterSchema).default([]),
  /** Packages going to this destination */
  packages: z.array(ShipmentManifestPackageSchema).default([])
});
var ShipmentManifestShipperSchema = z.object({
  facilityLicenseNumber: z.string().nullable().optional(),
  facilityName: z.string().nullable().optional()
});
var ShipmentManifestPayloadSnapshotSchema = z.object({
  flow: DeliveryShipmentManifestFlowSchema.default("templates_outgoing"),
  transferTypeName: z.string().nullable().optional(),
  name: z.string().nullable().optional(),
  plannedRoute: z.string().nullable().optional(),
  shipper: ShipmentManifestShipperSchema,
  /** Root-level convenience transporter (first driver) */
  rootTransporter: ShipmentManifestTransporterSchema.optional(),
  /** Single destination per manifest (1 manifest = 1 destination = 1 buyer license) */
  destinations: z.array(ShipmentManifestDestinationSchema).max(1).default([]),
  metadata: z.record(z.unknown()).default({})
});
var DeliveryShipmentManifestReadinessIssueSchema = z.object({
  code: z.string().min(1),
  message: z.string().min(1),
  field: z.string().optional(),
  details: z.record(z.unknown()).optional()
});
var DeliveryShipmentManifestReadinessSchema = z.object({
  isReady: z.boolean(),
  blockingErrors: z.array(DeliveryShipmentManifestReadinessIssueSchema),
  warnings: z.array(DeliveryShipmentManifestReadinessIssueSchema),
  info: z.array(DeliveryShipmentManifestReadinessIssueSchema).default([]),
  checkedAt: z.date(),
  metadata: z.record(z.unknown()).default({})
});
var DeliveryShipmentManifestSchema = z.object({
  id: z.string().uuid(),
  shipmentId: z.string().uuid(),
  destinationNodeId: z.string().uuid(),
  organizationId: z.string().uuid(),
  status: DeliveryShipmentManifestStatusSchema,
  version: z.number().int().positive(),
  metrcFlow: DeliveryShipmentManifestFlowSchema,
  metrcTransferId: z.number().int().positive().nullable(),
  metrcManifestNumber: z.string().nullable(),
  payloadSnapshot: ShipmentManifestPayloadSnapshotSchema,
  readinessSnapshot: DeliveryShipmentManifestReadinessSchema.nullable(),
  lastPreparedAt: z.date().nullable(),
  submittedAt: z.date().nullable(),
  departedAt: z.date().nullable(),
  arrivedAt: z.date().nullable(),
  voidedAt: z.date().nullable(),
  metadata: z.record(z.unknown()),
  createdAt: z.date(),
  updatedAt: z.date()
});
var CreateDeliveryShipmentManifestSchema = z.object({
  shipmentId: z.string().uuid(),
  destinationNodeId: z.string().uuid(),
  organizationId: z.string().uuid(),
  status: DeliveryShipmentManifestStatusSchema.default("draft"),
  version: z.number().int().positive().default(1),
  metrcFlow: DeliveryShipmentManifestFlowSchema.default("templates_outgoing"),
  metrcTransferId: z.number().int().positive().nullable().optional(),
  metrcManifestNumber: z.string().nullable().optional(),
  payloadSnapshot: ShipmentManifestPayloadSnapshotSchema,
  readinessSnapshot: DeliveryShipmentManifestReadinessSchema.nullable().optional(),
  lastPreparedAt: z.date().nullable().optional(),
  submittedAt: z.date().nullable().optional(),
  departedAt: z.date().nullable().optional(),
  arrivedAt: z.date().nullable().optional(),
  voidedAt: z.date().nullable().optional(),
  metadata: z.record(z.unknown()).optional()
});
var UpdateDeliveryShipmentManifestSchema = z.object({
  status: DeliveryShipmentManifestStatusSchema.optional(),
  version: z.number().int().positive().optional(),
  metrcTransferId: z.number().int().positive().nullable().optional(),
  metrcManifestNumber: z.string().nullable().optional(),
  payloadSnapshot: ShipmentManifestPayloadSnapshotSchema.optional(),
  readinessSnapshot: DeliveryShipmentManifestReadinessSchema.nullable().optional(),
  lastPreparedAt: z.date().nullable().optional(),
  submittedAt: z.date().nullable().optional(),
  departedAt: z.date().nullable().optional(),
  arrivedAt: z.date().nullable().optional(),
  voidedAt: z.date().nullable().optional(),
  metadata: z.record(z.unknown()).optional()
});
var DeliveryShipmentPlanNodeDtoSchema = DeliveryShipmentPlanNodeSchema.extend({
  arrivalDeadline: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  /** Resolved license number (e.g. "M-100112-002") — enriched at query time */
  licenseNumber: z.string().nullable().optional(),
  /** Resolved address line for display — enriched at query time */
  address: z.string().nullable().optional(),
  /** Resolved latitude from org_location or stock_location — for map rendering */
  latitude: z.number().nullable().optional(),
  /** Resolved longitude from org_location or stock_location — for map rendering */
  longitude: z.number().nullable().optional()
});
var DeliveryShipmentPartitionAllocationDtoSchema = DeliveryShipmentPartitionAllocationSchema.extend({
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});
var DeliveryShipmentManifestReadinessDtoSchema = DeliveryShipmentManifestReadinessSchema.extend({
  checkedAt: z.string().datetime()
});
var DeliveryShipmentManifestDtoSchema = DeliveryShipmentManifestSchema.extend({
  readinessSnapshot: DeliveryShipmentManifestReadinessDtoSchema.nullable(),
  lastPreparedAt: z.string().datetime().nullable(),
  submittedAt: z.string().datetime().nullable(),
  departedAt: z.string().datetime().nullable(),
  arrivedAt: z.string().datetime().nullable(),
  voidedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});

export {
  DeliveryShipmentPlanNodeTypeSchema,
  DeliveryShipmentPartitionAllocationStateSchema,
  DeliveryShipmentManifestStatusSchema,
  DeliveryShipmentManifestFlowSchema,
  NodeAddressSnapshotSchema,
  HubLayoverScheduleSchema,
  HubNodeMetadataSchema,
  DeliveryShipmentPlanNodeSchema,
  CreateDeliveryShipmentPlanNodeSchema,
  UpdateDeliveryShipmentPlanNodeSchema,
  DeliveryShipmentPartitionAllocationSchema,
  CreateDeliveryShipmentPartitionAllocationSchema,
  UpdateDeliveryShipmentPartitionAllocationSchema,
  ShipmentManifestTransporterDetailSchema,
  ShipmentManifestTransporterSchema,
  ShipmentManifestPackageSchema,
  ShipmentManifestDestinationSchema,
  ShipmentManifestShipperSchema,
  ShipmentManifestPayloadSnapshotSchema,
  DeliveryShipmentManifestReadinessIssueSchema,
  DeliveryShipmentManifestReadinessSchema,
  DeliveryShipmentManifestSchema,
  CreateDeliveryShipmentManifestSchema,
  UpdateDeliveryShipmentManifestSchema,
  DeliveryShipmentPlanNodeDtoSchema,
  DeliveryShipmentPartitionAllocationDtoSchema,
  DeliveryShipmentManifestReadinessDtoSchema,
  DeliveryShipmentManifestDtoSchema
};
